import React, { useState, useMemo, useEffect, useLayoutEffect, useRef, useCallback, memo } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Customized,
} from 'recharts';
import FilterDropdown from '../FilterDropdown';
import FilterModal from '../FilterModal';
// ChartHelpButton removed for preset view (no title icon)
import { formatDateShort, formatLargeNumber, formatExactNumber, getISOWeekNumber, formatWeekTooltipLabel, getCleanOptions, getSmartYDomain, applyMultiSelectFilter, hasPlaceholderRows } from '../../utils/analyticsHelpers';
import { useChartFontSize } from '../../context/ChartFontSizeContext';
import { useTheme } from '../../../context/ThemeContext';
import { getChartTheme } from '../../utils/chartTheme';
import { useSyncedChartScroll } from './useSyncedChartScroll';
import { useInitialScrollToLatest } from './useInitialScrollToLatest';
import {
  getHorizontalScrollChartDimensions,
  getChartScrollYAxisGutterPx,
  CHART_SCROLL_Y_AXIS_GUTTER_LEFT_MARGIN,
  CHART_SCROLL_Y_AXIS_GUTTER_LEFT_MARGIN_PHONE,
} from './chartHorizontalScrollLayout';
import { getChartYAxisWidth, getYAxisDomainLabels } from './chartStyleUtils';
import usePersistWidgetSnapshot from '../../hooks/usePersistWidgetSnapshot';
import { useWidgetOptionsPersistence } from '../../hooks/useWidgetOptionsPersistence';
import { VolumeByOriginScorecards } from '../analytics/KpiScorecards';
import ChartFullscreenTitleControl from '../analytics/ChartFullscreenTitleControl';
import { PresetChartTitle, PresetFilterStrip, PresetChartLegend, presetChartBodyClass } from '../analytics/PresetChartChrome';
import { useIsBelowLg, useIsPhone, getResponsiveChartMargin } from '../../hooks/useMediaQuery';
import { ChartTooltipFrame, ChartTooltipColorDot } from './ChartTooltipContent';
import { useChartTooltipTrigger } from './useChartTooltipTrigger';
import { singleSelectionLabel } from '../../../utils/filterLabels';

// Bar sizing inside the chart (separate from scroll min-width constants)
const CURRENT_YEAR_BAR_SIZE = 25; // Bar width for current year
const LAST_YEAR_BAR_SIZE = 22; // Bar width for last year (half of current)
const TOOLTIP_X_OFFSET_PX = 20;
const TOOLTIP_VIEWPORT_PADDING_PX = 12;
const TOOLTIP_Z_INDEX = 1000;
const TOOLTIP_ANIMATION_MS = 400;
const Y_AXIS_TICK_COUNT = 6;
const VOLUME_CHART_MARGIN_Y_DEFAULT = 20;
const VOLUME_GUTTER_MARGIN_Y_MIN = 2;
const VOLUME_GUTTER_MARGIN_Y_MAX = 120;

/** Vertical SVG line: true if x is (nearly) constant (Y-axis spine / vertical grid). */
function isVerticalSvgLine(line) {
  if (!line || line.tagName !== 'line') return false;
  const x1 = parseFloat(line.getAttribute('x1') || '0');
  const x2 = parseFloat(line.getAttribute('x2') || '0');
  return Number.isFinite(x1) && Number.isFinite(x2) && Math.abs(x1 - x2) < 0.5;
}

function verticalLineSpanPx(line) {
  if (!line) return null;
  const y1 = parseFloat(line.getAttribute('y1') || 'NaN');
  const y2 = parseFloat(line.getAttribute('y2') || 'NaN');
  if (!Number.isFinite(y1) || !Number.isFinite(y2)) return null;
  return Math.abs(y2 - y1);
}

const StackedBarTooltip = memo(function StackedBarTooltip({
  active,
  payload,
  label,
  coordinate,
  scrollViewportRef,
  plotAreaRef,
  theme,
  currentYear,
  lastYearLabel,
}) {
  const tooltipRef = useRef(null);
  const [portalStyle, setPortalStyle] = useState(null);
  const pinnedTopRef = useRef(null);
  const pinnedLabelRef = useRef(null);

  useLayoutEffect(() => {
    if (!active || !payload?.length || !coordinate || !scrollViewportRef?.current || !plotAreaRef?.current) {
      pinnedTopRef.current = null;
      pinnedLabelRef.current = null;
      setPortalStyle(null);
      return undefined;
    }

    const scrollEl = scrollViewportRef.current;
    const plotEl = plotAreaRef.current;

    const updatePosition = () => {
      const tipEl = tooltipRef.current;
      const chartWrapper = scrollEl.querySelector('.recharts-wrapper');
      const rechartsTip = scrollEl.querySelector('.recharts-tooltip-wrapper');
      if (!chartWrapper || !tipEl) return;

      const plotRect = plotEl.getBoundingClientRect();
      const chartRect = chartWrapper.getBoundingClientRect();
      const tooltipWidth = tipEl.offsetWidth || 0;

      // Pin Y per bar column so it does not track the mouse vertically within a stack.
      let top = pinnedTopRef.current;
      if (rechartsTip) {
        const measuredTop = rechartsTip.getBoundingClientRect().top - plotRect.top;
        if (label !== pinnedLabelRef.current) {
          pinnedLabelRef.current = label;
          pinnedTopRef.current = measuredTop;
          top = measuredTop;
        } else if (top == null) {
          top = measuredTop;
        }
      }

      const viewportLeft = scrollEl.scrollLeft;
      const viewportRight = viewportLeft + scrollEl.clientWidth;
      const cx = typeof coordinate.x === 'number' ? coordinate.x : 0;

      // Default: sit to the right of the bar; flip left of pointer when near the right edge.
      let contentLeft = cx + TOOLTIP_X_OFFSET_PX;
      if (contentLeft + tooltipWidth + TOOLTIP_VIEWPORT_PADDING_PX > viewportRight) {
        contentLeft = cx - TOOLTIP_X_OFFSET_PX - tooltipWidth;
      }

      const minLeft = viewportLeft + TOOLTIP_VIEWPORT_PADDING_PX;
      const maxLeft = viewportRight - tooltipWidth - TOOLTIP_VIEWPORT_PADDING_PX;
      if (maxLeft >= minLeft) {
        contentLeft = Math.max(minLeft, Math.min(contentLeft, maxLeft));
      }

      // X follows coordinate.x in chart space (and scroll); Y stays pinned per column.
      const left = chartRect.left - plotRect.left + contentLeft;

      setPortalStyle((prev) => {
        if (
          prev &&
          Math.abs(prev.left - left) < 0.5 &&
          Math.abs(prev.top - (top ?? prev.top)) < 0.5
        ) {
          return prev;
        }
        return { left, top: top ?? prev?.top ?? 0 };
      });
    };

    updatePosition();
    scrollEl.addEventListener('scroll', updatePosition, { passive: true });
    window.addEventListener('resize', updatePosition);

    return () => {
      scrollEl.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [active, coordinate?.x, label, payload, scrollViewportRef, plotAreaRef]);

  if (!active || !payload || payload.length === 0 || !plotAreaRef?.current) return null;

  // Separate current year and last year entries, sorted by volume DESC
  const currentYearEntries = payload
    .filter((p) => !String(p.dataKey || '').startsWith('ly_'))
    .sort((a, b) => b.value - a.value);
  const lastYearEntries = payload
    .filter((p) => String(p.dataKey || '').startsWith('ly_'))
    .sort((a, b) => b.value - a.value);

  // Use per-bar year if available (weekly mode), otherwise fall back to chart-level label
  const barData = payload[0]?.payload;
  const tooltipCurrentYear = barData?.currentBarYear ?? currentYear;
  const tooltipLastYear = barData?.lastBarYear ?? lastYearLabel;
  const tooltipCurrentYearTotal = barData?.total ?? 0;
  const tooltipLastYearTotal = barData?.lastYearTotal ?? 0;

  const normalizePctToPercent = (raw) => {
    const n = Number(raw);
    if (!Number.isFinite(n)) return null;
    // Accept either 0..1 share or 0..100 percent from backend.
    if (n >= 0 && n <= 1) return n * 100;
    if (n >= 0 && n <= 100) return n;
    return null;
  };

  const formatPercentText = (pct0to100) => {
    const pct = Number(pct0to100);
    if (!Number.isFinite(pct)) return '';
    return `${pct.toFixed(1).replace(/\.0$/, '')}%`;
  };

  const pctFromBackendOrCompute = (originKey, value, total, isLastYear = false) => {
    const pctKey = isLastYear ? `ly_${originKey}__pct` : `${originKey}__pct`;
    const fromBackend = normalizePctToPercent(barData?.[pctKey]);
    if (fromBackend != null) return formatPercentText(fromBackend);

    const v = Number(value);
    const t = Number(total);
    if (!Number.isFinite(v) || !Number.isFinite(t) || t <= 0) return '';
    return formatPercentText((v / t) * 100);
  };

  // Format label to match Commodity Planner: "Week N · MM/DD/YYYY - MM/DD/YYYY"
  const displayLabel = (barData?.iso_week != null && barData?.currentBarYear != null)
    ? formatWeekTooltipLabel(barData.iso_week, barData.currentBarYear)
    : label;

  const isVisible = Boolean(portalStyle);
  const translateX = portalStyle?.left ?? -9999;
  const translateY = portalStyle?.top ?? -9999;

  return createPortal(
    <div
      ref={tooltipRef}
      className="pointer-events-none"
      style={{
        position: 'absolute',
        left: 0,
        top: 0,
        zIndex: TOOLTIP_Z_INDEX,
        opacity: isVisible ? 1 : 0,
        transform: `translate3d(${translateX}px, ${translateY}px, 0)`,
        transition: `transform ${TOOLTIP_ANIMATION_MS}ms ease, opacity 200ms ease`,
        willChange: 'transform, opacity',
      }}
    >
      <ChartTooltipFrame theme={theme} className="max-w-xs">
        <p className="mb-2 text-sm font-medium" style={{ color: theme.tooltipText }}>{displayLabel}</p>

        {/* Current Year — hide 0-value origins (not in this period's top 3) */}
        {currentYearEntries.some((e) => e.value > 0) && (
          <div className="mb-2">
            <p className="mb-1 text-xs" style={{ color: theme.tooltipSecondaryText }}>
              {tooltipCurrentYear}
              {tooltipCurrentYearTotal > 0 ? ` (Total: ${formatExactNumber(tooltipCurrentYearTotal)})` : ''}
            </p>
            {currentYearEntries.map((entry, index) => {
              if (entry.value === 0) return null;
              const pct = pctFromBackendOrCompute(entry.dataKey, entry.value, tooltipCurrentYearTotal, false);
              return (
                <div key={index} className="flex items-center gap-2 text-xs">
                  <ChartTooltipColorDot color={entry.color} />
                  <span style={{ color: entry.color }}>
                    {entry.name}: {formatExactNumber(entry.value)}{pct ? ` (${pct})` : ''}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        {/* Last Year */}
        {lastYearEntries.length > 0 && lastYearEntries.some((e) => e.value > 0) && (
          <div>
            <p className="mb-1 text-xs" style={{ color: theme.tooltipSecondaryText }}>
              {tooltipLastYear}
              {tooltipLastYearTotal > 0 ? ` (Total: ${formatExactNumber(tooltipLastYearTotal)})` : ''}
            </p>
            {lastYearEntries.map((entry, index) => {
              if (entry.value === 0) return null;
              const originName = String(entry.dataKey || '').replace('ly_', '');
              const pct = pctFromBackendOrCompute(originName, entry.value, tooltipLastYearTotal, true);
              return (
                <div key={index} className="flex items-center gap-2 text-xs">
                  <ChartTooltipColorDot color={entry.color} />
                  <span style={{ color: entry.color }}>
                    {originName}: {formatExactNumber(entry.value)}{pct ? ` (${pct})` : ''}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </ChartTooltipFrame>
    </div>,
    plotAreaRef.current
  );
});

// Get all unique origins from data
const getAllOrigins = (data) => {
  return [...new Set(data.map((item) => item.origin).filter(Boolean))].sort();
};

// Get all unique types from data
const getAllTypes = (data) => {
  return [...new Set(data.map((item) => item.type).filter(Boolean))].sort();
};

// Get top 5 origins from a dataset by total volume
const getTop5FromData = (data) => {
  const totals = {};
  data.forEach(item => {
    if (item.origin) totals[item.origin] = (totals[item.origin] || 0) + (Number(item.vol) || 0);
  });
  return getTop3FromOriginsMap(totals);
};

/** Top 3 origins for a single period (day/week) from an { origin: volume } map. */
const getTop3FromOriginsMap = (originsMap) => {
  if (!originsMap || typeof originsMap !== 'object') return [];
  return Object.entries(originsMap)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5)
    .map(([origin]) => origin);
};

/** Union of per-period top-3 origins across grouped periods, sorted by total volume. */
const unionPerPeriodTop3Origins = (periodGroups, allData = []) => {
  const unionSet = new Set();
  periodGroups.forEach((g) => {
    getTop3FromOriginsMap(g.origins).forEach((o) => unionSet.add(o));
  });
  if (unionSet.size === 0) return [];
  const totals = {};
  unionSet.forEach((o) => {
    totals[o] = 0;
  });
  allData.forEach((item) => {
    if (item.origin && unionSet.has(item.origin)) {
      totals[item.origin] = (totals[item.origin] || 0) + (Number(item.vol) || 0);
    }
  });
  return [...unionSet].sort((a, b) => (totals[b] || 0) - (totals[a] || 0));
};

// Default colors for origins — 10 entries to cover union of up to two years' top-3 per period (matching mobile)
const defaultColors = ['#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#F44336', '#00BCD4', '#E91E63', '#009688', '#FF5722', '#3F51B5'];
const lastYearColors = ['#8B6F47', '#6B8E9F', '#B8860B', '#9370DB', '#CD5C5C', '#5F9EA0', '#A0522D', '#4682B4', '#8B4513', '#6A5ACD'];

/**
 * Generate pattern ID for last year bars
 */
const getPatternId = (index) => `pattern-lastyear-${index}`;

/**
 * Generate pattern JSX based on type
 */
const createPatternJSX = (patternId, color, patternType) => {
  if (patternType === 'stripes') {
    return (
      <pattern
        key={patternId}
        id={patternId}
        patternUnits="userSpaceOnUse"
        width="8"
        height="8"
      >
        <rect width="8" height="8" fill={color} />
        <path
          d="M0,0 L8,8 M-2,2 L2,6 M6,2 L10,6"
          stroke="rgba(255,255,255,0.3)"
          strokeWidth="1.5"
          strokeLinecap="square"
        />
      </pattern>
    );
  } else if (patternType === 'dots') {
    return (
      <pattern
        key={patternId}
        id={patternId}
        patternUnits="userSpaceOnUse"
        width="6"
        height="6"
      >
        <rect width="6" height="6" fill={color} />
        <circle cx="3" cy="3" r="1.5" fill="rgba(255,255,255,0.4)" />
      </pattern>
    );
  } else {
    // Default: diagonal stripes
    return (
      <pattern
        key={patternId}
        id={patternId}
        patternUnits="userSpaceOnUse"
        width="6"
        height="6"
        patternTransform="rotate(45)"
      >
        <rect width="6" height="6" fill={color} />
        <line
          x1="0"
          y1="0"
          x2="0"
          y2="6"
          stroke="rgba(255,255,255,0.3)"
          strokeWidth="1.5"
        />
      </pattern>
    );
  }
};

/**
 * Custom bar shape with pattern support
 */
const PatternedBar = (props) => {
  const { fill, patternId, x, y, width, height, radius, stroke, strokeWidth, className } = props;
  const toNum = (v) => {
    const n = typeof v === 'number' ? v : parseFloat(String(v ?? '0'));
    return Number.isFinite(n) ? n : 0;
  };

  const buildRoundedRectPath = (x0, y0, w0, h0, r) => {
    const x = toNum(x0);
    const y = toNum(y0);
    const w = Math.max(0, toNum(w0));
    const h = Math.max(0, toNum(h0));
    const [rtl, rtr, rbr, rbl] = r;
    const maxR = Math.min(w / 2, h / 2);
    const tl = Math.min(Math.max(0, toNum(rtl)), maxR);
    const tr = Math.min(Math.max(0, toNum(rtr)), maxR);
    const br = Math.min(Math.max(0, toNum(rbr)), maxR);
    const bl = Math.min(Math.max(0, toNum(rbl)), maxR);

    // Clockwise path, supports any mix of 0/non-zero corner radii.
    return [
      `M ${x + tl} ${y}`,
      `H ${x + w - tr}`,
      tr ? `A ${tr} ${tr} 0 0 1 ${x + w} ${y + tr}` : `L ${x + w} ${y}`,
      `V ${y + h - br}`,
      br ? `A ${br} ${br} 0 0 1 ${x + w - br} ${y + h}` : `L ${x + w} ${y + h}`,
      `H ${x + bl}`,
      bl ? `A ${bl} ${bl} 0 0 1 ${x} ${y + h - bl}` : `L ${x} ${y + h}`,
      `V ${y + tl}`,
      tl ? `A ${tl} ${tl} 0 0 1 ${x + tl} ${y}` : `L ${x} ${y}`,
      'Z',
    ].join(' ');
  };

  const resolvedFill = patternId ? `url(#${patternId})` : fill;

  // Recharts `radius` can be an array: [topLeft, topRight, bottomRight, bottomLeft].
  // SVG <rect> `rx`/`ry` do NOT accept an array, so render a path for per-corner radii.
  if (Array.isArray(radius)) {
    const r = radius.length === 4 ? radius : [radius[0], radius[0], radius[0], radius[0]];
    const d = buildRoundedRectPath(x, y, width, height, r);
    return (
      <path
        d={d}
        stroke={stroke}
        strokeWidth={strokeWidth}
        className={className}
        fill={resolvedFill}
      />
    );
  }

  const r = toNum(radius);
  return (
    <rect
      x={x}
      y={y}
      width={width}
      height={height}
      rx={r}
      ry={r}
      stroke={stroke}
      strokeWidth={strokeWidth}
      className={className}
      fill={resolvedFill}
    />
  );
};

/**
 * Legend item with pattern support
 */
const PatternedLegendItem = ({ color, patternId, patternType, size = 12 }) => {
  // Use a unique ID for the legend pattern to avoid conflicts
  const legendPatternId = `legend-${patternId}`;
  
  return (
    <svg width={size} height={size} style={{ flexShrink: 0 }}>
      <defs>
        {createPatternJSX(legendPatternId, color, patternType)}
      </defs>
      <rect width={size} height={size} fill={`url(#${legendPatternId})`} rx="2" />
    </svg>
  );
};

const BAR_LABEL_TOP_OFFSET_PX = 6;
const BAR_LABEL_STACK_GAP_PX = 4;
const BAR_LABEL_PAIR_TOP_Y_EPSILON_PX = 1;
const VOLUME_CHART_PLOT_CHROME_PX = 60; // top + bottom margin + x-axis
const DEBUG_VOLUME_BAR_LABELS = true;

function debugVolumeBarLabel(reason, data) {
  if (!DEBUG_VOLUME_BAR_LABELS) return;
  console.log('[VolumeBarLabel]', reason, data);
}

function estimateLabelTextWidth(text, fontSize) {
  return String(text).length * fontSize * 0.58;
}

function getBarPairTotals(pointData, barSeries, value) {
  const total = Number(pointData?.total ?? (barSeries === 'current' ? value : 0)) || 0;
  const lastYearTotal = Number(pointData?.lastYearTotal ?? (barSeries === 'lastYear' ? value : 0)) || 0;
  return {
    total,
    lastYearTotal,
    thisValue: barSeries === 'current' ? total : lastYearTotal,
    otherValue: barSeries === 'current' ? lastYearTotal : total,
  };
}

function valueToPlotHeightPx(value, volumeYDomain, plotInnerHeight) {
  const numericValue = Number(value);
  const yMin = Array.isArray(volumeYDomain) ? Number(volumeYDomain[0]) : 0;
  const yMax = Array.isArray(volumeYDomain) ? Number(volumeYDomain[1]) : 0;
  if (
    !Number.isFinite(numericValue) ||
    numericValue <= 0 ||
    plotInnerHeight <= 0 ||
    !Number.isFinite(yMin) ||
    !Number.isFinite(yMax) ||
    yMax <= yMin
  ) {
    return 0;
  }
  return ((numericValue - yMin) / (yMax - yMin)) * plotInnerHeight;
}

/**
 * Total label above each stacked bar. When current vs last year tops are closer than
 * one label line, the shorter bar's label is dropped so it does not overwrite the taller.
 */
const CustomBarLabel = (props) => {
  const {
    x,
    y,
    width,
    value,
    index,
    fontSize,
    theme,
    barSeries,
    volumeYDomain,
    hasLastYearData,
    useCompactBars,
    pointData,
    plotInnerHeight,
    categorySlotWidth,
    barTopYByIndexRef,
  } = props;

  if (barTopYByIndexRef && Number.isFinite(index)) {
    if (!barTopYByIndexRef.current[index]) {
      barTopYByIndexRef.current[index] = {};
    }
    barTopYByIndexRef.current[index][barSeries] = y;
  }

  const otherBarTopY = barTopYByIndexRef?.current?.[index]?.[
    barSeries === 'current' ? 'lastYear' : 'current'
  ];

  const debugBase = {
    index,
    barSeries,
    value,
    x,
    y,
    width,
    plotInnerHeight,
    categorySlotWidth,
    hasLastYearData,
    useCompactBars,
    volumeYDomain,
    pointData: pointData
      ? { total: pointData.total, lastYearTotal: pointData.lastYearTotal, date: pointData.date }
      : null,
    otherBarTopY,
    propKeys: Object.keys(props),
  };

  if (!value || value === 0) {
    debugVolumeBarLabel('skipped: no value', debugBase);
    return null;
  }

  const labelFontSize = fontSize - 2;
  const labelLineHeight = labelFontSize + 4;
  const formattedValue = formatExactNumber(value);
  const textWidth = estimateLabelTextWidth(formattedValue, labelFontSize);

  const { thisValue, otherValue } = getBarPairTotals(pointData, barSeries, value);
  let labelY = y - BAR_LABEL_TOP_OFFSET_PX;

  if (hasLastYearData && otherValue > 0 && thisValue > 0) {
    const thisBarPx = valueToPlotHeightPx(thisValue, volumeYDomain, plotInnerHeight);
    const otherBarPx = valueToPlotHeightPx(otherValue, volumeYDomain, plotInnerHeight);
    const topGapPx =
      otherBarTopY != null
        ? Math.abs(y - otherBarTopY)
        : Math.abs(thisBarPx - otherBarPx);
    const equalBarTops = otherBarTopY != null && topGapPx <= BAR_LABEL_PAIR_TOP_Y_EPSILON_PX;
    const thisBarIsShorter =
      otherBarTopY != null ? y > otherBarTopY + BAR_LABEL_PAIR_TOP_Y_EPSILON_PX : thisValue < otherValue;

    // Current-year labels render before last-year in the overlay layer; wait for both SVG tops.
    if (otherBarTopY != null && topGapPx <= labelLineHeight) {
      const topBarY = Math.min(y, otherBarTopY);
      const stackedLabelY =
        topBarY - BAR_LABEL_TOP_OFFSET_PX + labelLineHeight + BAR_LABEL_STACK_GAP_PX;

      if (equalBarTops) {
        // Same column height: current on the top line, last year one line below.
        if (barSeries === 'lastYear') {
          labelY = stackedLabelY;
        }
      } else if (thisBarIsShorter) {
        labelY = stackedLabelY;
      }
    }

    if (index < 3) {
      debugVolumeBarLabel('layout', {
        ...debugBase,
        formattedValue,
        textWidth,
        thisValue,
        otherValue,
        thisBarPx,
        otherBarPx,
        topGapPx,
        topGapSource: otherBarTopY != null ? 'svg-y' : 'value-estimate',
        labelLineHeight,
        labelY,
        equalBarTops,
        thisBarIsShorter,
      });
    }
  } else if (index < 3) {
    debugVolumeBarLabel('layout skipped: missing pair metrics', {
      ...debugBase,
      thisValue,
      otherValue,
    });
  }

  if (index < 3 && barSeries === 'current') {
    debugVolumeBarLabel('render', {
      ...debugBase,
      formattedValue,
      textWidth,
      labelY,
    });
  }

  return (
    <text
      x={x + width / 2}
      y={labelY}
      fill={theme?.barLabelFill || '#fff'}
      textAnchor="middle"
      fontSize={labelFontSize}
      fontWeight="500"
    >
      {formattedValue}
    </text>
  );
};

function findTopStackBarRects(formattedGraphicalItems, stackId, segmentDataKey) {
  if (!Array.isArray(formattedGraphicalItems)) return [];
  for (const entry of formattedGraphicalItems) {
    const barProps = entry?.item?.props;
    if (!barProps || barProps.stackId !== stackId || barProps.dataKey !== segmentDataKey) continue;
    const rects = entry?.props?.data;
    if (Array.isArray(rects)) return rects;
  }
  return [];
}

/**
 * Renders bar total labels after all bar geometry so labels paint on top (SVG has no z-index).
 */
function VolumeBarTotalLabelsLayer({
  formattedGraphicalItems,
  chartData,
  yKeys,
  hasLastYearData,
  fontSize,
  theme,
  volumeYDomain,
  useCompactBars,
  plotInnerHeight,
  categorySlotWidth,
  barTopYByIndexRef,
}) {
  if (!yKeys?.length || !chartData?.length || !formattedGraphicalItems?.length) return null;

  const topSegmentKey = yKeys[yKeys.length - 1];
  const currentRects = findTopStackBarRects(formattedGraphicalItems, 'currentYear', topSegmentKey);
  const lastYearRects = hasLastYearData
    ? findTopStackBarRects(formattedGraphicalItems, 'lastYear', `ly_${topSegmentKey}`)
    : [];

  barTopYByIndexRef.current = {};

  const sharedLabelProps = {
    fontSize,
    theme,
    volumeYDomain,
    hasLastYearData,
    useCompactBars,
    plotInnerHeight,
    categorySlotWidth,
    barTopYByIndexRef,
  };

  return (
    <g className="recharts-layer recharts-volume-bar-total-labels" style={{ pointerEvents: 'none' }}>
      {currentRects.map((rect, index) => {
        const pointData = chartData[index];
        const value = pointData?.total;
        if (!value) return null;
        return (
          <CustomBarLabel
            key={`volume-label-cy-${index}`}
            x={rect.x}
            y={rect.y}
            width={rect.width}
            value={value}
            index={index}
            barSeries="current"
            pointData={pointData}
            {...sharedLabelProps}
          />
        );
      })}
      {lastYearRects.map((rect, index) => {
        const pointData = chartData[index];
        const value = pointData?.lastYearTotal;
        if (!value) return null;
        return (
          <CustomBarLabel
            key={`volume-label-ly-${index}`}
            x={rect.x}
            y={rect.y}
            width={rect.width}
            value={value}
            index={index}
            barSeries="lastYear"
            pointData={pointData}
            {...sharedLabelProps}
          />
        );
      })}
    </g>
  );
}

/**
 * Volume by Origin - Stacked bar chart with last year comparison
 * Matches mobile BStackedBarChart behavior
 *
 * Top 3 origins logic (when "All Regions" selected and >3 origins exist):
 * - Each bar (day/week) uses that period's own top 3 origins by volume
 * - Legend shows the union of all origins that ever rank in a period's top 3
 * - Origins outside a period's top 3 roll into "Other regions" for that bar only
 * - Prevents seasonal leaders (e.g. Salinas in W23) from being hidden in "Other"
 *   because they are not in the full-range annual top 3
 */
const StackedBarChart = ({
  data = [],
  email = '',
  dateStart = null,
  dateEnd = null,
  commodity = '',
  forceFiltersOpen = false,
  presetScorecards = null,
  hidePresetScorecards = false,
  presetDescription = '',
  analyticsFullscreen = null,
  presetSingleChartFullscreen = false,
  hideLegend = false,
}) => {
  const { isReady: analyticsPrefsReady } = useWidgetOptionsPersistence('stacked-bar');
  const { t } = useTranslation();
  const { fontSize } = useChartFontSize();
  const isPhone = useIsPhone();
  const { isDark } = useTheme();
  const theme = getChartTheme(isDark);
  // Collapsed by default. The preset view keeps filters open from lg up via the
  // CSS gate on the strip below; smaller screens stay behind the Filters toggle.
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);
  const plotAreaRef = useRef(null);
  const [viewMode, setViewMode] = useState('daily');

  // Filter states
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedVarieties, setSelectedVarieties] = useState([]);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [selectedOrigins, setSelectedOrigins] = useState([]);

  // Track container width for scroll calculations
  useEffect(() => {
    const el = plotAreaRef.current;
    if (!el) return;

    let rafId = null;
    let rafRetryId = null;
    const updateSize = (rect) => {
      const bounds = rect ?? el.getBoundingClientRect();
      const width = bounds.width;
      const height = bounds.height;
      if (!Number.isFinite(width) || width <= 0) return;
      setContainerWidth((prev) => (Math.abs(prev - width) < 0.5 ? prev : width));
      if (Number.isFinite(height) && height > 0) {
        setContainerHeight((prev) => (Math.abs(prev - height) < 0.5 ? prev : height));
      }
    };

    rafId = window.requestAnimationFrame(() => updateSize());
    let attempts = 0;
    const retry = () => {
      attempts += 1;
      updateSize();
      if ((el.getBoundingClientRect().width || 0) <= 0 && attempts < 10) {
        rafRetryId = window.requestAnimationFrame(retry);
      }
    };
    rafRetryId = window.requestAnimationFrame(retry);

    let ro = null;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (entry?.contentRect) updateSize(entry.contentRect);
      });
      ro.observe(el);
    }

    const onResize = () => updateSize();
    window.addEventListener('resize', onResize);
    return () => {
      if (rafId != null) window.cancelAnimationFrame(rafId);
      if (rafRetryId != null) window.cancelAnimationFrame(rafRetryId);
      window.removeEventListener('resize', onResize);
      if (ro) ro.disconnect();
    };
  }, []);

  // Modal states
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [varietyModalOpen, setVarietyModalOpen] = useState(false);
  const [propertiesModalOpen, setPropertiesModalOpen] = useState(false);
  const [originModalOpen, setOriginModalOpen] = useState(false);
  const [groupByModalOpen, setGroupByModalOpen] = useState(false);
  const initializedRef = useRef(false);
  const skipVarietiesAllRef = useRef(false);
  const skipPropertiesAllRef = useRef(false);
  const skipOriginsAllRef = useRef(false);
  /** User hit Clear all — do not let sync effects immediately re-select everything when `all*` lists update. */
  const varietiesUserClearedRef = useRef(false);
  const propertiesUserClearedRef = useRef(false);
  const originsUserClearedRef = useRef(false);
  // Track when option lists first become available (0 → N) so stale empty snapshots
  // still default to select-all once, without undoing an in-session Clear all.
  const prevAllVarietiesCountRef = useRef(0);
  const prevAllPropertiesCountRef = useRef(0);

  // Reset init guard when commodity scope changes so defaults can apply for the new commodity.
  useLayoutEffect(() => {
    initializedRef.current = false;
    skipVarietiesAllRef.current = false;
    skipPropertiesAllRef.current = false;
    skipOriginsAllRef.current = false;
    varietiesUserClearedRef.current = false;
    propertiesUserClearedRef.current = false;
    originsUserClearedRef.current = false;
    prevAllVarietiesCountRef.current = 0;
    prevAllPropertiesCountRef.current = 0;
    // Clear multi-selects so the sync effects default-select ALL available options for the new commodity.
    setSelectedTypes([]);
    setSelectedVarieties([]);
    setSelectedProperties([]);
    setSelectedOrigins([]);
  }, [commodity]);

  // Get all types from data
  const allTypes = useMemo(() => getAllTypes(data), [data]);

  // Get varieties from type-filtered data (computed below after typeFilteredData)

  // Do NOT clear initializedRef in useEffect([data]): effects run after useLayoutEffect, so that
  // would run after Firestore hydrate (usePersistWidgetSnapshot) and let the init effect below
  // overwrite saved filters before they persist again.

  const hydrateStacked = useCallback(
    (s) => {
      initializedRef.current = true;
      const pickMultiOrAll = (arr, allowed) => {
        if (!allowed.length) return [];
        if (!Array.isArray(arr) || arr.length === 0) return [...allowed];
        const next = arr.filter((x) => allowed.includes(x));
        return next.length ? next : [...allowed];
      };
      // Non-empty saved subset → restore it (skip sync once).
      // Explicit empty [] → honor Clear all (userCleared).
      // Missing key → leave empty and let sync default to all when options exist.
      const hasSavedVarieties = Array.isArray(s.selectedVarieties) && s.selectedVarieties.length > 0;
      const explicitEmptyVarieties = Array.isArray(s.selectedVarieties) && s.selectedVarieties.length === 0;
      const hasSavedProperties = Array.isArray(s.selectedProperties) && s.selectedProperties.length > 0;
      const explicitEmptyProperties = Array.isArray(s.selectedProperties) && s.selectedProperties.length === 0;
      const hasSavedOrigins = Array.isArray(s.selectedOrigins) && s.selectedOrigins.length > 0;
      const explicitEmptyOrigins = Array.isArray(s.selectedOrigins) && s.selectedOrigins.length === 0;
      // Missing / null / non-array → no stored origin selection → default all.
      const missingOriginsSelection = !Object.prototype.hasOwnProperty.call(s, 'selectedOrigins')
        || s.selectedOrigins == null
        || !Array.isArray(s.selectedOrigins);
      skipVarietiesAllRef.current = hasSavedVarieties;
      skipPropertiesAllRef.current = hasSavedProperties;
      skipOriginsAllRef.current = hasSavedOrigins;
      varietiesUserClearedRef.current = explicitEmptyVarieties;
      propertiesUserClearedRef.current = explicitEmptyProperties;
      originsUserClearedRef.current = explicitEmptyOrigins;
      if (typeof s.filtersExpanded === 'boolean') setFiltersExpanded(s.filtersExpanded);
      if (s.viewMode === 'daily' || s.viewMode === 'weekly') setViewMode(s.viewMode);
      else setViewMode('weekly');
      setSelectedTypes(pickMultiOrAll(s.selectedTypes, allTypes));
      if (hasSavedVarieties) setSelectedVarieties(s.selectedVarieties);
      else if (explicitEmptyVarieties) setSelectedVarieties([]);
      if (hasSavedProperties) setSelectedProperties(s.selectedProperties);
      else if (explicitEmptyProperties) setSelectedProperties([]);
      const originsAllowed = getAllOrigins(data);
      if (hasSavedOrigins) {
        const kept = s.selectedOrigins.filter((o) => originsAllowed.includes(o));
        setSelectedOrigins(kept.length ? kept : originsAllowed);
      } else if (explicitEmptyOrigins) {
        setSelectedOrigins([]);
      } else if (missingOriginsSelection) {
        setSelectedOrigins(originsAllowed);
      }
    },
    [allTypes, data]
  );

  const snapshotStacked = useCallback(
    () => ({
      filtersExpanded,
      viewMode,
      selectedTypes,
      selectedVarieties,
      selectedProperties,
      selectedOrigins,
    }),
    [filtersExpanded, viewMode, selectedTypes, selectedVarieties, selectedProperties, selectedOrigins]
  );

  usePersistWidgetSnapshot(
    'stacked-bar',
    commodity,
    !!(data && data.length > 0 && allTypes.length > 0),
    hydrateStacked,
    snapshotStacked,
    [filtersExpanded, viewMode, selectedTypes, selectedVarieties, selectedProperties, selectedOrigins]
  );

  // Defaults only when there is no Firestore snapshot; after analytics state is fetched.
  useEffect(() => {
    if (!analyticsPrefsReady || !data || data.length === 0 || initializedRef.current) return;
    if (allTypes.length === 0) return;
    initializedRef.current = true;

    // Defaults (View 2 spec):
    // - Type: All
    // - Origin: All
    // - Group by: Week
    setViewMode('weekly');
    setSelectedTypes(allTypes);
    varietiesUserClearedRef.current = false;
    propertiesUserClearedRef.current = false;
    originsUserClearedRef.current = false;
    setSelectedVarieties([]);
    setSelectedProperties([]);
    setSelectedOrigins(getAllOrigins(data));
  }, [analyticsPrefsReady, data, allTypes]);

  // Filter data by type
  const typeFilteredData = useMemo(() => {
    return data.filter((item) => {
      if (!item.type) return true;
      if (selectedTypes.length === 0) return false;
      return selectedTypes.includes(item.type);
    });
  }, [data, selectedTypes]);

  // Get varieties from type-filtered data
  const allVarieties = useMemo(() => getCleanOptions(typeFilteredData, 'variety', commodity), [typeFilteredData, commodity]);

  // Movement-volume "properties" dimension (Fresh / For Processing / Frozen, etc.).
  // Options come from the (type-filtered) data; when the field is absent from the
  // payload this is [] and the filter is hidden and has no effect.
  const allProperties = useMemo(() => getCleanOptions(typeFilteredData, 'properties', commodity), [typeFilteredData, commodity]);

  // Sync variety selection with options for the current type(s); preserve Firestore/hydrated values (skip once).
  // After skip, prune to a valid subset — do not always replace with all varieties (Strict Mode / re-renders
  // would wipe a saved subset the same way origins did).
  useEffect(() => {
    if (skipVarietiesAllRef.current) {
      skipVarietiesAllRef.current = false;
      prevAllVarietiesCountRef.current = allVarieties.length;
      return;
    }
    if (allVarieties.length > 0) {
      const justAppeared = prevAllVarietiesCountRef.current === 0;
      prevAllVarietiesCountRef.current = allVarieties.length;
      if (justAppeared) {
        varietiesUserClearedRef.current = false;
        setSelectedVarieties(allVarieties);
        return;
      }
      setSelectedVarieties((prev) => {
        const kept = prev.filter((v) => allVarieties.includes(v));
        if (kept.length > 0) return kept;
        if (varietiesUserClearedRef.current) return prev;
        return allVarieties;
      });
    } else {
      prevAllVarietiesCountRef.current = 0;
    }
  }, [allVarieties]);

  // Sync properties selection with options; preserve hydrated selection (skip once) and honor Clear all.
  useEffect(() => {
    if (skipPropertiesAllRef.current) {
      skipPropertiesAllRef.current = false;
      prevAllPropertiesCountRef.current = allProperties.length;
      return;
    }
    if (allProperties.length > 0) {
      const justAppeared = prevAllPropertiesCountRef.current === 0;
      prevAllPropertiesCountRef.current = allProperties.length;
      if (justAppeared) {
        propertiesUserClearedRef.current = false;
        setSelectedProperties(allProperties);
        return;
      }
      setSelectedProperties((prev) => {
        const kept = prev.filter((p) => allProperties.includes(p));
        if (kept.length > 0) return kept;
        if (propertiesUserClearedRef.current) return prev;
        return allProperties;
      });
    } else {
      prevAllPropertiesCountRef.current = 0;
    }
  }, [allProperties]);

  // Filter data by variety and properties (both applied before day/week grouping so the
  // per-day and per-week sums downstream naturally respect the selections).
  const varietyFilteredData = useMemo(() => {
    const byVariety = applyMultiSelectFilter(typeFilteredData, 'variety', selectedVarieties, allVarieties, commodity);
    return applyMultiSelectFilter(byVariety, 'properties', selectedProperties, allProperties, commodity);
  }, [typeFilteredData, allVarieties, selectedVarieties, allProperties, selectedProperties, commodity]);

  // True while the plotted rows still include rows with no value for the dimension (the "All" state);
  // a chip must not name a single variety/property over such rows.
  const unknownRetained = useMemo(() => ({
    variety: hasPlaceholderRows(varietyFilteredData, 'variety', commodity),
    properties: hasPlaceholderRows(varietyFilteredData, 'properties', commodity),
  }), [varietyFilteredData, commodity]);

  // Separate current year and last year data (matching mobile logic)
  // Filter by dateStart and dateEnd to only show selected dates
  // When date range spans multiple years, show each year with its corresponding past year data
  const { currentYearData, lastYearData, currentYear, lastYearLabel } = useMemo(() => {
    if (varietyFilteredData.length === 0) {
      return { currentYearData: [], lastYearData: [], currentYear: new Date().getFullYear(), lastYearLabel: new Date().getFullYear() - 1 };
    }

    // Parse dateStart and dateEnd if provided
    let minDate = null;
    let maxDate = null;
    if (dateStart) {
      minDate = new Date(dateStart);
      minDate.setHours(0, 0, 0, 0); // Start of day
    }
    if (dateEnd) {
      maxDate = new Date(dateEnd);
      maxDate.setHours(23, 59, 59, 999); // End of day
    }

    // Check if date range spans multiple years
    let spansMultipleYears = false;
    let yearsInRange = [];
    if (minDate && maxDate) {
      const minYear = minDate.getFullYear();
      const maxYear = maxDate.getFullYear();
      spansMultipleYears = minYear !== maxYear;
      
      // Get all years in the range
      for (let year = minYear; year <= maxYear; year++) {
        yearsInRange.push(year);
      }
    }

    // If spanning multiple years, combine data from all years with their corresponding past year data
    if (spansMultipleYears && minDate && maxDate) {
      const allCurrentYearData = [];
      const allLastYearData = [];
      
      yearsInRange.forEach((year) => {
        // Determine the date range for this specific year
        let yearStartDate = null;
        let yearEndDate = null;
        
        if (year === minDate.getFullYear()) {
          // First year: start from minDate
          yearStartDate = new Date(minDate);
        } else {
          // Subsequent years: start from Jan 1 of that year
          yearStartDate = new Date(year, 0, 1);
          yearStartDate.setHours(0, 0, 0, 0);
        }
        
        if (year === maxDate.getFullYear()) {
          // Last year: end at maxDate
          yearEndDate = new Date(maxDate);
        } else {
          // Previous years: end at Dec 31 of that year
          yearEndDate = new Date(year, 11, 31);
          yearEndDate.setHours(23, 59, 59, 999);
        }
        
        // Get current year data for this year's portion
        const yearData = varietyFilteredData.filter((item) => {
          const itemDate = item.date_string instanceof Date ? item.date_string : new Date(item.date_string);
          const itemYear = itemDate.getFullYear();
          return itemYear === year && itemDate >= yearStartDate && itemDate <= yearEndDate;
        });
        allCurrentYearData.push(...yearData);
        
        // Get corresponding past year data (same month-day range, previous year)
        const lastYear = year - 1;
        const lastYearStartMonth = yearStartDate.getMonth();
        const lastYearStartDay = yearStartDate.getDate();
        const lastYearEndMonth = yearEndDate.getMonth();
        const lastYearEndDay = yearEndDate.getDate();
        
        const lastYearData = varietyFilteredData.filter((item) => {
          const itemDate = item.date_string instanceof Date ? item.date_string : new Date(item.date_string);
          const itemYear = itemDate.getFullYear();
          if (itemYear !== lastYear) return false;
          
          const itemMonth = itemDate.getMonth();
          const itemDay = itemDate.getDate();
          
          // Match the same month-day range from previous year
          if (lastYearStartMonth === lastYearEndMonth) {
            return itemMonth === lastYearStartMonth && itemDay >= lastYearStartDay && itemDay <= lastYearEndDay;
          } else {
            return (itemMonth > lastYearStartMonth || (itemMonth === lastYearStartMonth && itemDay >= lastYearStartDay)) &&
                   (itemMonth < lastYearEndMonth || (itemMonth === lastYearEndMonth && itemDay <= lastYearEndDay));
          }
        });
        allLastYearData.push(...lastYearData);
      });
      
      const minYear = Math.min(...yearsInRange);
      const maxYear = Math.max(...yearsInRange);
      // Note: currentYear may be a string (e.g. "2025-2026") for cross-year ranges — use only for display
      const currentYearLabel = minYear === maxYear ? maxYear : `${minYear}-${maxYear}`;
      const lastYearLbl = minYear === maxYear ? maxYear - 1 : `${minYear - 1}-${maxYear - 1}`;
      return {
        currentYearData: allCurrentYearData,
        lastYearData: allLastYearData,
        currentYear: currentYearLabel,
        lastYearLabel: lastYearLbl
      };
    }

    // Single year: maintain year-over-year comparison
    const maxYear = Math.max(...varietyFilteredData.map((item) => item.year || new Date(item.date_string).getFullYear()));
    const currentYear = maxYear;
    const lastYear = currentYear - 1;

    // Filter current year data by date range if provided
    let currentYearData = varietyFilteredData.filter((item) => {
      const itemYear = item.year || new Date(item.date_string).getFullYear();
      if (itemYear !== currentYear) return false;
      
      // If date range is provided, filter by it
      if (minDate || maxDate) {
        const itemDate = item.date_string instanceof Date ? item.date_string : new Date(item.date_string);
        if (minDate && itemDate < minDate) return false;
        if (maxDate && itemDate > maxDate) return false;
      }
      return true;
    });

    // Filter last year data to only include corresponding date range
    let lastYearData = varietyFilteredData.filter((item) => {
      const itemYear = item.year || new Date(item.date_string).getFullYear();
      if (itemYear !== lastYear) return false;
      
      // If date range is provided, filter last year data to match the month-day range
      if (minDate && maxDate) {
        const itemDate = item.date_string instanceof Date ? item.date_string : new Date(item.date_string);
        const minMonth = minDate.getMonth();
        const minDay = minDate.getDate();
        const maxMonth = maxDate.getMonth();
        const maxDay = maxDate.getDate();
        const itemMonth = itemDate.getMonth();
        const itemDay = itemDate.getDate();
        
        // Match the same month-day range from previous year
        if (minMonth === maxMonth) {
          // Same month: check day range
          return itemMonth === minMonth && itemDay >= minDay && itemDay <= maxDay;
        } else {
          // Different months: check if date is within the month-day range
          return (itemMonth > minMonth || (itemMonth === minMonth && itemDay >= minDay)) &&
                 (itemMonth < maxMonth || (itemMonth === maxMonth && itemDay <= maxDay));
        }
      }
      return true;
    });

    // Fallback: if date filtering left currentYearData empty but data exists,
    // treat maxYear data as "current" without date filtering (e.g., Kiwifruit with future dates)
    if (currentYearData.length === 0 && varietyFilteredData.length > 0) {
      const fallbackCurrent = varietyFilteredData.filter(item => {
        const yr = item.year || new Date(item.date_string).getFullYear();
        return yr === maxYear;
      });
      const fallbackLast = varietyFilteredData.filter(item => {
        const yr = item.year || new Date(item.date_string).getFullYear();
        return yr === maxYear - 1;
      });
      return { currentYearData: fallbackCurrent, lastYearData: fallbackLast,
               currentYear: maxYear, lastYearLabel: maxYear - 1 };
    }

    return { currentYearData, lastYearData, currentYear, lastYearLabel: currentYear - 1 };
  }, [varietyFilteredData, dateStart, dateEnd]);

  // Origin options come from the full commodity dataset — independent of Type/Variety/Properties.
  // Changing those filters must NOT rewrite the Origin selection (only manual Origin changes do).
  const allOrigins = useMemo(() => getAllOrigins(data), [data]);

  // Default Origin to all once; afterward only prune origins that left the commodity dataset entirely.
  useEffect(() => {
    if (skipOriginsAllRef.current) {
      skipOriginsAllRef.current = false;
      return;
    }
    if (allOrigins.length === 0) return;
    setSelectedOrigins((prev) => {
      if (prev.length === 0) {
        if (originsUserClearedRef.current) return prev;
        return allOrigins;
      }
      const kept = prev.filter((o) => allOrigins.includes(o));
      if (kept.length === prev.length) return prev;
      if (kept.length > 0) return kept;
      if (originsUserClearedRef.current) return prev;
      return allOrigins;
    });
  }, [allOrigins]);

  // Determine which origins to show (top 3 + others if all selected)
  const { originsToShow, useTop3Logic } = useMemo(() => {
    if (selectedOrigins.length === 0) {
      return { originsToShow: [], useTop3Logic: false };
    }

    const isAllSelected = selectedOrigins.length === allOrigins.length;

    if (isAllSelected && allOrigins.length > 3) {
      // Per-period top 3 is applied in chartData; legend union is built there too.
      return { originsToShow: [], useTop3Logic: true };
    }

    const originTotals = [...currentYearData, ...lastYearData].reduce((acc, item) => {
      if (selectedOrigins.includes(item.origin)) {
        acc[item.origin] = (acc[item.origin] || 0) + (Number(item.vol) || 0);
      }
      return acc;
    }, {});
    const sorted = [...selectedOrigins].sort((a, b) => (originTotals[b] || 0) - (originTotals[a] || 0));
    return { originsToShow: sorted, useTop3Logic: false };
  }, [selectedOrigins, allOrigins, currentYearData, lastYearData]);

  // Transform data for stacked bars with last year comparison
  const { chartData, yKeys, originColors, lastYearOriginColors, lastYearPatternIds, lastYearPatternTypes } = useMemo(() => {
    if (selectedOrigins.length === 0 || (currentYearData.length === 0 && lastYearData.length === 0)) {
      return { chartData: [], yKeys: [], originColors: {}, lastYearOriginColors: {}, lastYearPatternIds: {}, lastYearPatternTypes: [] };
    }

    const pickPctField = (item) => {
      // Backend may provide one of these. Accept 0..1 share or 0..100 percent.
      return (
        item?.pct_of_total ??
        item?.percent_of_total ??
        item?.pct_total ??
        item?.share_of_total ??
        item?.share ??
        item?.pct
      );
    };

    // Helper: sum volumes for origins NOT in a top3 list
    const sumOtherRegions = (originsMap, top3) => {
      return Object.entries(originsMap).reduce((sum, [origin, vol]) => {
        return top3.includes(origin) ? sum : sum + vol;
      }, 0);
    };

    // Local calendar-day key (avoids timezone shifting from toISOString)
    const toDayKey = (input) => {
      const d = input instanceof Date ? new Date(input) : new Date(input);
      d.setHours(0, 0, 0, 0);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    };

    const parseToDayStart = (input) => {
      const d = input instanceof Date ? new Date(input) : new Date(input);
      d.setHours(0, 0, 0, 0);
      return d;
    };

    if (viewMode === 'weekly') {
      const getWeek = (item) => {
        if (item.iso_week != null) return Number(item.iso_week);
        if (item.iso_week_number != null) return Number(item.iso_week_number);
        const d = item.date_string instanceof Date ? item.date_string : new Date(item.date_string);
        return getISOWeekNumber(d);
      };
      // Use ISO year to avoid calendar/ISO week mismatch (e.g. Dec 29 2025 = ISO week 1 of 2026)
      const getISOYear = (item) => {
        if (item.iso_year != null) return Number(item.iso_year);
        const d = item.date_string instanceof Date ? item.date_string : new Date(item.date_string);
        const month = d.getMonth();
        const isoWeek = getWeek(item);
        if (isoWeek === 1 && month === 11) return d.getFullYear() + 1;
        if (isoWeek >= 52 && month === 0) return d.getFullYear() - 1;
        return item.year != null ? Number(item.year) : d.getFullYear();
      };

      // Group ALL origins per week (no pre-filtering into otherRegions)
      const currentYearGrouped = {};
      currentYearData.forEach((item) => {
        const year = getISOYear(item);
        const iso_week = getWeek(item);
        const key = `${year}_${iso_week}`;
        if (!currentYearGrouped[key]) {
          currentYearGrouped[key] = { year, iso_week, origins: {}, originPct: {} };
        }
        const dailyVol = Number(item.vol) || 0;
        if (item.origin) {
          currentYearGrouped[key].origins[item.origin] = (currentYearGrouped[key].origins[item.origin] || 0) + dailyVol;
          const pct = pickPctField(item);
          if (pct != null) currentYearGrouped[key].originPct[item.origin] = pct;
        }
      });

      const lastYearByWeek = {};
      lastYearData.forEach((item) => {
        const year = getISOYear(item);
        const iso_week = getWeek(item);
        const key = `${year}_${iso_week}`;
        if (!lastYearByWeek[key]) {
          lastYearByWeek[key] = { year, iso_week, origins: {}, originPct: {} };
        }
        const dailyVol = Number(item.vol) || 0;
        if (item.origin) {
          lastYearByWeek[key].origins[item.origin] = (lastYearByWeek[key].origins[item.origin] || 0) + dailyVol;
          const pct = pickPctField(item);
          if (pct != null) lastYearByWeek[key].originPct[item.origin] = pct;
        }
      });

      const currentYearPeriods = Object.values(currentYearGrouped);
      const lastYearPeriods = Object.values(lastYearByWeek);
      const legendOrigins = useTop3Logic
        ? unionPerPeriodTop3Origins(
            [...currentYearPeriods, ...lastYearPeriods],
            [...currentYearData, ...lastYearData],
          )
        : [...originsToShow];

      const yKeysWeekly = [...legendOrigins];
      if (useTop3Logic) {
        yKeysWeekly.push('Other regions');
      }

      // Build chart entries — per-period top 3 when "All Regions" is selected
      const chartDataWeekly = currentYearPeriods
        .map((g) => {
          const lastYearWeekKey = `${g.year - 1}_${g.iso_week}`;
          const lastYearItem = lastYearByWeek[lastYearWeekKey];
          const total = Object.values(g.origins).reduce((s, v) => s + v, 0);
          const lyTotal = lastYearItem ? Object.values(lastYearItem.origins).reduce((s, v) => s + v, 0) : 0;

          const result = {
            date: `W${g.iso_week}`,
            rawDate: null,
            sortKey: g.year * 100 + g.iso_week,
            total,
            lastYearTotal: lyTotal,
            currentBarYear: g.year,
            lastBarYear: g.year - 1,
            iso_week: g.iso_week,
          };

          if (useTop3Logic) {
            const weekCurTop3 = getTop3FromOriginsMap(g.origins);
            const weekLyTop3 = lastYearItem ? getTop3FromOriginsMap(lastYearItem.origins) : [];
            legendOrigins.forEach((origin) => {
              result[origin] = weekCurTop3.includes(origin) ? (g.origins[origin] || 0) : 0;
              if (weekCurTop3.includes(origin) && g.originPct?.[origin] != null) {
                result[`${origin}__pct`] = g.originPct[origin];
              }
            });
            result['Other regions'] = sumOtherRegions(g.origins, weekCurTop3);

            if (lastYearItem) {
              legendOrigins.forEach((origin) => {
                result[`ly_${origin}`] = weekLyTop3.includes(origin) ? (lastYearItem.origins[origin] || 0) : 0;
                if (weekLyTop3.includes(origin) && lastYearItem.originPct?.[origin] != null) {
                  result[`ly_${origin}__pct`] = lastYearItem.originPct[origin];
                }
              });
              result['ly_Other regions'] = sumOtherRegions(lastYearItem.origins, weekLyTop3);
            } else {
              legendOrigins.forEach((origin) => { result[`ly_${origin}`] = 0; });
              result['ly_Other regions'] = 0;
            }
          } else {
            legendOrigins.forEach((origin) => {
              result[origin] = g.origins[origin] || 0;
              if (g.originPct?.[origin] != null) result[`${origin}__pct`] = g.originPct[origin];
            });
            legendOrigins.forEach((origin) => {
              result[`ly_${origin}`] = lastYearItem?.origins[origin] || 0;
              if (lastYearItem?.originPct?.[origin] != null) result[`ly_${origin}__pct`] = lastYearItem.originPct[origin];
            });
          }
          return result;
        });

      chartDataWeekly.sort((a, b) => a.sortKey - b.sortKey);

      const originColorsW = {};
      const lastYearOriginColorsW = {};
      const lastYearPatternIdsW = {};
      const lastYearPatternTypesW = ['stripes', 'dots', 'stripes', 'dots', 'stripes', 'dots'];
      yKeysWeekly.forEach((key, index) => {
        if (key === 'Other regions') {
          originColorsW[key] = '#9E9E9E';
          lastYearOriginColorsW[key] = '#8B7D6B';
          lastYearPatternIdsW[key] = getPatternId(index);
        } else {
          originColorsW[key] = defaultColors[index % defaultColors.length];
          lastYearOriginColorsW[key] = lastYearColors[index % lastYearColors.length];
          lastYearPatternIdsW[key] = getPatternId(index);
        }
      });
      return { chartData: chartDataWeekly, yKeys: yKeysWeekly, originColors: originColorsW, lastYearOriginColors: lastYearOriginColorsW, lastYearPatternIds: lastYearPatternIdsW, lastYearPatternTypes: lastYearPatternTypesW };
    }

    // Group current year data by date (daily) — store ALL origins per day
    const currentYearGrouped = currentYearData.reduce((acc, item) => {
      const dayStart = parseToDayStart(item.date_string);
      const dateKey = toDayKey(dayStart);

      if (!acc[dateKey]) {
        acc[dateKey] = { date: dayStart, origins: {}, originPct: {} };
      }
      if (item.origin) {
        acc[dateKey].origins[item.origin] = (acc[dateKey].origins[item.origin] || 0) + (Number(item.vol) || 0);
        const pct = pickPctField(item);
        if (pct != null) acc[dateKey].originPct[item.origin] = pct;
      }
      return acc;
    }, {});

    // Group last year data by month-day for alignment — store ALL origins
    const lastYearByMonthDay = lastYearData.reduce((acc, item) => {
      const date = item.date_string instanceof Date ? item.date_string : new Date(item.date_string);
      const key = `${date.getMonth() + 1}-${date.getDate()}`;

      if (!acc[key]) {
        acc[key] = { origins: {}, originPct: {}, date: date };
      }
      if (item.origin) {
        acc[key].origins[item.origin] = (acc[key].origins[item.origin] || 0) + item.vol;
        const pct = pickPctField(item);
        if (pct != null) acc[key].originPct[item.origin] = pct;
      }
      return acc;
    }, {});

    const currentYearPeriodsDaily = Object.values(currentYearGrouped);
    const lastYearPeriodsDaily = Object.values(lastYearByMonthDay);
    const legendOriginsDaily = useTop3Logic
      ? unionPerPeriodTop3Origins(
          [...currentYearPeriodsDaily, ...lastYearPeriodsDaily],
          [...currentYearData, ...lastYearData],
        )
      : [...originsToShow];

    const yKeys = [...legendOriginsDaily];
    if (useTop3Logic) {
      yKeys.push('Other regions');
    }

    // Build a complete daily range so missing days still appear on X axis
    const determineRange = () => {
      if (dateStart && dateEnd) {
        return { start: parseToDayStart(dateStart), end: parseToDayStart(dateEnd) };
      }
      const allDates = currentYearData
        .map((d) => parseToDayStart(d.date_string))
        .filter((d) => !Number.isNaN(d.getTime()));
      if (allDates.length === 0) return { start: null, end: null };
      const start = new Date(Math.min(...allDates.map((d) => d.getTime())));
      const end = new Date(Math.max(...allDates.map((d) => d.getTime())));
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);
      return { start, end };
    };

    const { start: rangeStart, end: rangeEnd } = determineRange();
    const dayStarts = [];
    if (rangeStart && rangeEnd) {
      const cursor = new Date(rangeStart);
      const endTime = rangeEnd.getTime();
      while (cursor.getTime() <= endTime) {
        dayStarts.push(new Date(cursor));
        cursor.setDate(cursor.getDate() + 1);
      }
    }

    // Transform to chart data format — per-period top 3 when "All Regions" is selected.
    const chartData = (dayStarts.length > 0 ? dayStarts : currentYearPeriodsDaily.map((v) => v.date))
      .map((dayStart) => {
        const date = new Date(dayStart);
        date.setHours(0, 0, 0, 0);
        const dayKey = toDayKey(date);
        const item = currentYearGrouped[dayKey] || { date, origins: {} };
        const monthDay = `${date.getMonth() + 1}-${date.getDate()}`;
        const lastYearItem = lastYearByMonthDay[monthDay];
        const total = Object.values(item.origins).reduce((s, v) => s + v, 0);
        const lyTotal = lastYearItem ? Object.values(lastYearItem.origins).reduce((s, v) => s + v, 0) : 0;

        const result = {
          date: formatDateShort(date),
          rawDate: date,
          total,
          lastYearTotal: lyTotal,
        };

        if (useTop3Logic) {
          const dayCurTop3 = getTop3FromOriginsMap(item.origins);
          const dayLyTop3 = lastYearItem ? getTop3FromOriginsMap(lastYearItem.origins) : [];
          legendOriginsDaily.forEach((origin) => {
            result[origin] = dayCurTop3.includes(origin) ? (item.origins[origin] || 0) : 0;
            if (dayCurTop3.includes(origin) && item.originPct?.[origin] != null) {
              result[`${origin}__pct`] = item.originPct[origin];
            }
          });
          result['Other regions'] = sumOtherRegions(item.origins, dayCurTop3);

          if (lastYearItem) {
            legendOriginsDaily.forEach((origin) => {
              result[`ly_${origin}`] = dayLyTop3.includes(origin) ? (lastYearItem.origins[origin] || 0) : 0;
              if (dayLyTop3.includes(origin) && lastYearItem.originPct?.[origin] != null) {
                result[`ly_${origin}__pct`] = lastYearItem.originPct[origin];
              }
            });
            result['ly_Other regions'] = sumOtherRegions(lastYearItem.origins, dayLyTop3);
          } else {
            legendOriginsDaily.forEach((origin) => { result[`ly_${origin}`] = 0; });
            result['ly_Other regions'] = 0;
          }
        } else {
          legendOriginsDaily.forEach((origin) => {
            result[origin] = item.origins[origin] || 0;
            if (item.originPct?.[origin] != null) result[`${origin}__pct`] = item.originPct[origin];
          });
          legendOriginsDaily.forEach((origin) => {
            result[`ly_${origin}`] = lastYearItem?.origins[origin] || 0;
            if (lastYearItem?.originPct?.[origin] != null) result[`ly_${origin}__pct`] = lastYearItem.originPct[origin];
          });
        }

        return result;
      });

    chartData.sort((a, b) => a.rawDate - b.rawDate);

    // Assign colors (matching mobile)
    const originColors = {};
    const lastYearOriginColors = {};
    const lastYearPatternIds = {};
    const lastYearPatternTypes = ['stripes', 'dots', 'stripes', 'dots', 'stripes', 'dots'];

    yKeys.forEach((key, index) => {
      if (key === 'Other regions') {
        originColors[key] = '#9E9E9E';
        lastYearOriginColors[key] = '#8B7D6B';
        lastYearPatternIds[key] = getPatternId(index);
      } else {
        originColors[key] = defaultColors[index % defaultColors.length];
        lastYearOriginColors[key] = lastYearColors[index % lastYearColors.length];
        lastYearPatternIds[key] = getPatternId(index);
      }
    });

    return { chartData, yKeys, originColors, lastYearOriginColors, lastYearPatternIds, lastYearPatternTypes };
  }, [currentYearData, lastYearData, selectedOrigins, originsToShow, useTop3Logic, viewMode, dateStart, dateEnd]);

  // Check if we have last year data
  const hasLastYearData = useMemo(() => {
    return chartData.some((d) => d.lastYearTotal > 0);
  }, [chartData]);

  const volumeYDomain = useMemo(
    () => getSmartYDomain(chartData, ['total', 'lastYearTotal']),
    [chartData]
  );

  // Phones size the volume axis to the labels it actually draws instead of the
  // fixed 72px, which is mostly empty at the smaller phone tick size.
  //
  // Preset views only: the widget dashboard renders a rotated axis title in
  // that same column (the `forceFiltersOpen ? undefined : ...` label below).
  // Rotated about its centre, that title's horizontal extent sits mid-column,
  // so narrowing the column would run it straight into the tick labels.
  const compactYAxis = isPhone && forceFiltersOpen;
  const volumeYAxisWidth = useMemo(
    () =>
      getChartYAxisWidth({
        isPhone: compactYAxis,
        fontSize,
        labels: getYAxisDomainLabels(volumeYDomain, formatLargeNumber),
      }),
    [compactYAxis, fontSize, volumeYDomain]
  );
  const scrollYAxisGutterPx = getChartScrollYAxisGutterPx({
    isPhone: compactYAxis,
    yAxisWidth: volumeYAxisWidth,
  });

  // Calculate if horizontal scrolling is needed and chart width
  // Show more bars for shorter date ranges (e.g., 30 days) by using compact bar width
  const { needsScroll, chartWidth, useCompactBars } = useMemo(() => {
    return getHorizontalScrollChartDimensions({
      dataPointCount: chartData.length,
      containerWidth,
      categoryMultiplier: hasLastYearData ? 2 : 1,
    });
  }, [chartData.length, hasLastYearData, containerWidth]);

  const plotInnerHeight = useMemo(
    () => Math.max(0, containerHeight - VOLUME_CHART_PLOT_CHROME_PX),
    [containerHeight]
  );

  const categorySlotWidth = useMemo(() => {
    if (chartData.length === 0) return 0;
    const span = needsScroll ? chartWidth : containerWidth;
    return span > 0 ? span / chartData.length : 0;
  }, [chartData.length, needsScroll, chartWidth, containerWidth]);

  /** Gutter Y-axis chart: vertical margins tuned so axis spine length + endpoints match main chart vertical grid lines. */
  const volumeGridChartRef = useRef(null);
  const volumeYAxisChartRef = useRef(null);
  const [gutterPlotMarginsY, setGutterPlotMarginsY] = useState({
    top: VOLUME_CHART_MARGIN_Y_DEFAULT,
    bottom: VOLUME_CHART_MARGIN_Y_DEFAULT,
  });

  const volumeYAxisSyncLayoutKey = useMemo(
    () =>
      [
        needsScroll,
        chartWidth,
        containerWidth,
        chartData.length,
        yKeys.join('\u0000'),
        `${volumeYDomain?.[0]}-${volumeYDomain?.[1]}`,
        fontSize,
        filtersExpanded,
        viewMode,
        hasLastYearData,
      ].join('|'),
    [
      needsScroll,
      chartWidth,
      containerWidth,
      chartData.length,
      yKeys,
      volumeYDomain,
      fontSize,
      filtersExpanded,
      viewMode,
      hasLastYearData,
    ]
  );

  useLayoutEffect(() => {
    if (!needsScroll) {
      setGutterPlotMarginsY((p) =>
        p.top === VOLUME_CHART_MARGIN_Y_DEFAULT && p.bottom === VOLUME_CHART_MARGIN_Y_DEFAULT
          ? p
          : { top: VOLUME_CHART_MARGIN_Y_DEFAULT, bottom: VOLUME_CHART_MARGIN_Y_DEFAULT }
      );
      return;
    }
    setGutterPlotMarginsY((p) =>
      p.top === VOLUME_CHART_MARGIN_Y_DEFAULT && p.bottom === VOLUME_CHART_MARGIN_Y_DEFAULT
        ? p
        : { top: VOLUME_CHART_MARGIN_Y_DEFAULT, bottom: VOLUME_CHART_MARGIN_Y_DEFAULT }
    );
  }, [needsScroll, volumeYAxisSyncLayoutKey]);

  useLayoutEffect(() => {
    if (!needsScroll) return;

    let cancelled = false;
    const measureAndSync = () => {
      if (cancelled) return;
      const gridRoot = volumeGridChartRef.current;
      const axisRoot = volumeYAxisChartRef.current;
      if (!gridRoot || !axisRoot) return;

      const gridLines = gridRoot.querySelectorAll('.recharts-cartesian-grid-vertical line');
      let gridLine = null;
      for (let i = 0; i < gridLines.length; i++) {
        if (isVerticalSvgLine(gridLines[i])) {
          gridLine = gridLines[i];
          break;
        }
      }

      const axisLines = axisRoot.querySelectorAll('line.recharts-cartesian-axis-line');
      let axisLine = null;
      for (let i = 0; i < axisLines.length; i++) {
        if (isVerticalSvgLine(axisLines[i])) {
          axisLine = axisLines[i];
          break;
        }
      }

      if (!gridLine || !axisLine) return;
      const gridSpan = verticalLineSpanPx(gridLine);
      const axisSpan = verticalLineSpanPx(axisLine);
      if (gridSpan == null || axisSpan == null) return;

      const lengthDiff = gridSpan - axisSpan;
      if (Number.isFinite(lengthDiff) && Math.abs(lengthDiff) >= 0.5) {
        setGutterPlotMarginsY((prev) => {
          const half = lengthDiff / 2;
          const top = Math.min(
            VOLUME_GUTTER_MARGIN_Y_MAX,
            Math.max(VOLUME_GUTTER_MARGIN_Y_MIN, prev.top - half)
          );
          const bottom = Math.min(
            VOLUME_GUTTER_MARGIN_Y_MAX,
            Math.max(VOLUME_GUTTER_MARGIN_Y_MIN, prev.bottom - half)
          );
          if (Math.abs(top - prev.top) < 0.25 && Math.abs(bottom - prev.bottom) < 0.25) return prev;
          return { top, bottom };
        });
        return;
      }

      const gTop = gridLine.getBoundingClientRect().top;
      const aTop = axisLine.getBoundingClientRect().top;
      const deltaTop = aTop - gTop;
      if (!Number.isFinite(deltaTop) || Math.abs(deltaTop) < 0.5) return;

      setGutterPlotMarginsY((prev) => {
        const nextTop = prev.top - deltaTop;
        const nextBottom = prev.bottom + deltaTop;
        const top = Math.min(VOLUME_GUTTER_MARGIN_Y_MAX, Math.max(VOLUME_GUTTER_MARGIN_Y_MIN, nextTop));
        const bottom = Math.min(VOLUME_GUTTER_MARGIN_Y_MAX, Math.max(VOLUME_GUTTER_MARGIN_Y_MIN, nextBottom));
        if (Math.abs(top - prev.top) < 0.25 && Math.abs(bottom - prev.bottom) < 0.25) return prev;
        return { top, bottom };
      });
    };

    const raf = requestAnimationFrame(() => {
      requestAnimationFrame(measureAndSync);
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [needsScroll, volumeYAxisSyncLayoutKey, gutterPlotMarginsY.top, gutterPlotMarginsY.bottom]);

  const handleMultiSelect = (item, selected, setSelected) => {
    if (selected.includes(item)) {
      setSelected(selected.filter((i) => i !== item));
    } else {
      setSelected([...selected, item]);
    }
  };

  const getTypeDisplayText = () => {
    if (selectedTypes.length === 0) return t('filters.selectTypes');
    return singleSelectionLabel(selectedTypes, allTypes)
      ?? (selectedTypes.length === allTypes.length ? t('filters.allTypes') : selectedTypes.join(', '));
  };

  const getVarietyDisplayText = () => {
    if (selectedVarieties.length === 0) return t('filters.selectVarieties');
    return singleSelectionLabel(selectedVarieties, allVarieties, undefined, { unknownRetained: unknownRetained.variety })
      ?? (selectedVarieties.length === allVarieties.length
        ? t('filters.allVarieties')
        : t('filters.nSelected', { count: selectedVarieties.length }));
  };

  const getPropertiesDisplayText = () => {
    if (selectedProperties.length === 0) return t('filters.selectProperties');
    return singleSelectionLabel(selectedProperties, allProperties, undefined, { unknownRetained: unknownRetained.properties })
      ?? (selectedProperties.length === allProperties.length
        ? t('filters.allProperties')
        : t('filters.nSelected', { count: selectedProperties.length }));
  };

  const getOriginDisplayText = () => {
    if (selectedOrigins.length === 0) return t('filters.selectOrigins');
    return singleSelectionLabel(selectedOrigins, allOrigins)
      ?? (selectedOrigins.length === allOrigins.length
        ? t('filters.allRegions')
        : t('filters.nSelected', { count: selectedOrigins.length }));
  };

  const scrollViewportRef = useRef(null);
  const barTopYByIndexRef = useRef({});
  useEffect(() => {
    barTopYByIndexRef.current = {};
  }, [chartData, viewMode]);

  useSyncedChartScroll(scrollViewportRef, 'price-pulse-scroll');
  // Open on the most recent weeks rather than the far-left oldest ones.
  useInitialScrollToLatest(scrollViewportRef, {
    enabled: needsScroll && chartData.length > 0 && selectedOrigins.length > 0,
    syncGroupId: 'price-pulse-scroll',
    resetKey: commodity,
  });
  const { trigger: tooltipTrigger, tooltipDismissKey } = useChartTooltipTrigger(scrollViewportRef);
  const isBelowLg = useIsBelowLg();

  const helpDescription = "Stacked bar chart showing daily volume breakdown by origin. Compare current year vs last year to identify supply shifts between regions.";

  // Single-chart fullscreen: below lg the overlay scrolls, so these boxes grow
  // with their content instead of being squeezed to 0px, and the plot keeps a
  // real height. See the `.preset-fullscreen-*` block in index.css.
  const fsFill = presetSingleChartFullscreen ? ' preset-fullscreen-fill' : '';
  const fsPlot = presetSingleChartFullscreen ? ' preset-fullscreen-plot' : '';

  if (!data || data.length === 0) {
    return (
      <div className="h-full flex flex-col bg-transparent">
        <PresetChartTitle
          forceFiltersOpen={forceFiltersOpen}
          title={t('charts.volumeByOrigin.title')}
          theme={theme}
          fontSize={fontSize}
          className={forceFiltersOpen ? 'px-4 pt-4' : 'border-y py-2 px-4'}
        />
        <div className="flex-1 flex items-center justify-center">
          <p className="text-center" style={{ fontSize, color: theme.noDataText }}>
            {t('charts.volumeByOrigin.noData')}<br />{t('charts.selectAnotherTimeframe')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={
        (forceFiltersOpen
          ? presetSingleChartFullscreen
            ? 'w-full h-full min-h-0 flex-1' + fsFill
            : 'w-full'
          : 'h-full') + ' flex flex-col bg-transparent'
      }
    >
      <div className={`flex-1 flex flex-col min-h-0${fsFill} ${presetChartBodyClass(forceFiltersOpen, presetSingleChartFullscreen)}`}>
      {/* Title */}
      <PresetChartTitle
        forceFiltersOpen={forceFiltersOpen}
        title={t('charts.volumeByOrigin.title')}
        theme={theme}
        fontSize={fontSize}
        right={
          <ChartFullscreenTitleControl
            analyticsFullscreen={analyticsFullscreen}
            presetSingleChartFullscreen={presetSingleChartFullscreen}
          />
        }
      />

      {/* Filters / Cards / Chart */}
        {/* Filters */}
        <div className="flex-shrink-0">
          {/* Filters Header — collapsible in the widget dashboard, and below lg
              in the preset view too; lg:hidden once forceFiltersOpen since
              desktop preset keeps the strip open via the CSS gate below. */}
          <button
            onClick={() => setFiltersExpanded(!filtersExpanded)}
            className={`w-full flex items-center justify-between py-2 border-b ${forceFiltersOpen ? 'lg:hidden' : ''}`}
            style={{ borderColor: theme.borderSecondary }}
          >
            <span className="font-medium" style={{ fontSize: fontSize - 2, color: theme.secondaryText }}>{t('filters.filters')}</span>
            {filtersExpanded ? <ChevronUp size={16} style={{ color: theme.secondaryText }} /> : <ChevronDown size={16} style={{ color: theme.secondaryText }} />}
          </button>

          {/* Filter Dropdowns */}
          {(forceFiltersOpen || filtersExpanded) && (
            <div className={forceFiltersOpen ? (filtersExpanded ? 'max-lg:pt-3' : 'max-lg:hidden') : undefined}>
            <PresetFilterStrip
              forceFiltersOpen={forceFiltersOpen}
              presetSingleChartFullscreen={presetSingleChartFullscreen}
              analyticsFullscreen={analyticsFullscreen}
              titleHasFullscreenControl
              gridClassName="pt-2 space-y-2"
            >
            {forceFiltersOpen ? (
              <>
                <FilterDropdown
                  label={t('filters.type')}
                  value={getTypeDisplayText()}
                  onClick={() => setTypeModalOpen(true)}
                  compact
                />

                {allVarieties.length > 0 && (
                  <FilterDropdown
                    label={t('filters.variety')}
                    value={getVarietyDisplayText()}
                    onClick={() => setVarietyModalOpen(true)}
                    compact
                  />
                )}

                {allProperties.length > 0 && (
                  <FilterDropdown
                    label={t('filters.properties')}
                    value={getPropertiesDisplayText()}
                    onClick={() => setPropertiesModalOpen(true)}
                    compact
                  />
                )}

                {allOrigins.length > 0 && (
                  <FilterDropdown
                    label={t('filters.origin')}
                    value={getOriginDisplayText()}
                    onClick={() => setOriginModalOpen(true)}
                    compact
                  />
                )}

                <FilterDropdown
                  label={t('charts.volumeByOrigin.groupBy')}
                  value={viewMode === 'weekly' ? t('charts.volumeByOrigin.week') : t('charts.volumeByOrigin.day')}
                  onClick={() => setGroupByModalOpen(true)}
                  compact
                />
              </>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                  <FilterDropdown
                    label={t('filters.type')}
                    value={getTypeDisplayText()}
                    onClick={() => setTypeModalOpen(true)}
                  />

                  {allVarieties.length > 0 && (
                    <FilterDropdown
                      label={t('filters.variety')}
                      value={getVarietyDisplayText()}
                      onClick={() => setVarietyModalOpen(true)}
                    />
                  )}

                  {allProperties.length > 0 && (
                    <FilterDropdown
                      label={t('filters.properties')}
                      value={getPropertiesDisplayText()}
                      onClick={() => setPropertiesModalOpen(true)}
                    />
                  )}

                  {allOrigins.length > 0 && (
                    <FilterDropdown
                      label={t('filters.origin')}
                      value={getOriginDisplayText()}
                      onClick={() => setOriginModalOpen(true)}
                    />
                  )}

                  <FilterDropdown
                    label={t('charts.volumeByOrigin.groupBy')}
                    value={viewMode === 'weekly' ? t('charts.volumeByOrigin.week') : t('charts.volumeByOrigin.day')}
                    onClick={() => setGroupByModalOpen(true)}
                  />
                </div>
              </>
            )}
            </PresetFilterStrip>
            </div>
          )}
        </div>

        {/* Cards */}
        {forceFiltersOpen && !hidePresetScorecards ? (
          <div className="flex-shrink-0">
            <VolumeByOriginScorecards
              apiParams={{ email, commodity, dateStart, dateEnd }}
              dims={{
                type: selectedTypes,
                variety: selectedVarieties,
                origin: selectedOrigins,
              }}
            />
          </div>
        ) : presetScorecards ? (
          <div className="flex-shrink-0">{presetScorecards}</div>
        ) : null}

        {/* Chart */}
        <div
          className={
            (forceFiltersOpen && !presetSingleChartFullscreen ? '' : 'flex-1 min-h-0' + fsFill) +
            ' flex flex-col overflow-visible bg-white dark:bg-[#191919] rounded-lg'
          }
        >
          {/* Fixed-height plot box (`preset-chart-plot`). The legend below is a
              sibling of this box, not a child: it is flex-shrink-0, so a
              many-origin legend would consume the whole box and squeeze the
              flex-1 plot to 0px, hiding the chart while its legend stayed. */}
          <div
            className={
              (forceFiltersOpen && !presetSingleChartFullscreen
                ? 'preset-chart-plot'
                : 'flex-1' + fsPlot) + ' min-h-0 flex flex-col'
            }
          >
          {presetDescription ? (
            <div className="px-4 py-3 flex-shrink-0">
              <p className="text-[12px] text-[#828382] dark:text-gray-400 text-center">
                {(() => {
                  const commodityLabel = commodity
                    ? commodity
                        .split(' ')
                        .filter(Boolean)
                        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                        .join(' ')
                    : 'this commodity';

                  const isMeaningful = (v) => v != null && v !== '' && v !== 'N/A' && v !== 'Not Classified';
                  const parts = [];

                  // Commodity attributes only (origins intentionally omitted)
                  const typeText = getTypeDisplayText();
                  if (isMeaningful(typeText)) parts.push(typeText);
                  if (allVarieties.length > 0) {
                    const varietyText = getVarietyDisplayText();
                    if (isMeaningful(varietyText)) parts.push(varietyText);
                  }

                  const summary = parts.filter(Boolean).join(' · ');
                  return summary ? `${commodityLabel} · ${summary}` : commodityLabel;
                })()}
              </p>
            </div>
          ) : null}
          <div className="flex-1 min-h-0 flex flex-row overflow-visible">
          {chartData.length > 0 && selectedOrigins.length > 0 && needsScroll ? (
            <div
              className="relative flex-shrink-0 h-full"
              style={{
                width: scrollYAxisGutterPx,
                backgroundColor: theme.widgetBg,
              }}
            >
              {/* Hide y-axis label in preset view (matches design) */}
              {!forceFiltersOpen && (
                <span
                  className="pointer-events-none absolute z-[1] left-1 top-1/2 -translate-y-1/2 origin-center -rotate-90 whitespace-nowrap"
                  style={{ fontSize, color: theme.axisLabelFill }}
                >
                  {t('filters.volumeLbs')}
                </span>
              )}
              <div ref={volumeYAxisChartRef} className="h-full w-full min-h-0">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  margin={{
                    top: gutterPlotMarginsY.top,
                    right: 2,
                    left: compactYAxis
                      ? CHART_SCROLL_Y_AXIS_GUTTER_LEFT_MARGIN_PHONE
                      : CHART_SCROLL_Y_AXIS_GUTTER_LEFT_MARGIN,
                    bottom: gutterPlotMarginsY.bottom,
                  }}
                  barGap={2}
                  barCategoryGap={useCompactBars ? '8%' : '15%'}
                >
                  <XAxis dataKey="date" hide />
                  <YAxis
                    width={volumeYAxisWidth}
                    stroke={theme.axisStroke}
                    tickCount={Y_AXIS_TICK_COUNT}
                    domain={volumeYDomain}
                    allowDataOverflow={false}
                    tickFormatter={formatLargeNumber}
                    tick={{ fontSize, fill: theme.tickFill }}
                  />
                  <Bar dataKey="total" fill="transparent" isAnimationActive={false} />
                </BarChart>
              </ResponsiveContainer>
              </div>
            </div>
          ) : null}
          <div className="relative flex-1 min-w-0 min-h-0 flex flex-col overflow-visible" ref={plotAreaRef}>
          {chartData.length > 0 && selectedOrigins.length > 0 ? (
            <div
              className="chart-x-scroll h-full min-h-0 overflow-x-auto overflow-y-hidden"
              ref={scrollViewportRef}
              style={{
                scrollbarWidth: 'thin',
                scrollbarColor: '#666 transparent',
                WebkitOverflowScrolling: 'touch', // Smooth scrolling on iOS/Safari
              }}
            >
              <div style={{ width: needsScroll ? chartWidth : '100%', height: '100%', minWidth: needsScroll ? chartWidth : 'auto' }}>
                <div ref={needsScroll ? volumeGridChartRef : undefined} className="h-full w-full min-h-0">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={chartData}
                    margin={getResponsiveChartMargin({ isBelowLg, isPhone: compactYAxis, needsScroll })}
                    barGap={2}
                    barCategoryGap={useCompactBars ? "8%" : "15%"}
                  >
                      <defs>
                        {yKeys.map((key, index) => {
                          const patternId = lastYearPatternIds[key];
                          const color = lastYearOriginColors[key] || '#8B7D6B';
                          const patternType = lastYearPatternTypes[index % lastYearPatternTypes.length];
                          return createPatternJSX(patternId, color, patternType);
                        })}
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={theme.gridColor} vertical horizontal />
                      <XAxis
                        dataKey="date"
                        stroke={theme.axisStroke}
                        angle={-45}
                        textAnchor="end"
                        height={40}
                        tick={{ fontSize, fill: theme.tickFill }}
                        tickFormatter={(v) => {
                          if (v == null) return '';
                          const s = String(v);
                          return s.includes('NaN') || s.includes('undefined') ? '' : s;
                        }}
                      />
                      {needsScroll ? (
                        <YAxis
                          hide
                          width={0}
                          domain={volumeYDomain}
                          tickCount={Y_AXIS_TICK_COUNT}
                          allowDataOverflow={false}
                        />
                      ) : (
                        <YAxis
                          width={volumeYAxisWidth}
                          stroke={theme.axisStroke}
                          tickCount={Y_AXIS_TICK_COUNT}
                          domain={volumeYDomain}
                          allowDataOverflow={false}
                          tickFormatter={formatLargeNumber}
                          tick={{ fontSize, fill: theme.tickFill }}
                          label={
                            forceFiltersOpen
                              ? undefined
                              : {
                                  value: t('filters.volumeLbs'),
                                  angle: -90,
                                  position: 'insideLeft',
                                  dx: -12,
                                  fill: theme.axisLabelFill,
                                  fontSize,
                                }
                          }
                        />
                      )}
                      <Tooltip
                        key={tooltipDismissKey}
                        trigger={tooltipTrigger}
                        throttleDelay={50}
                        animationDuration={TOOLTIP_ANIMATION_MS}
                        animationEasing="ease"
                        wrapperStyle={{ visibility: 'hidden', pointerEvents: 'none' }}
                        allowEscapeViewBox={{ x: true, y: true }}
                        content={(props) => (
                          <StackedBarTooltip
                            {...props}
                            scrollViewportRef={scrollViewportRef}
                            plotAreaRef={plotAreaRef}
                            theme={theme}
                            currentYear={currentYear}
                            lastYearLabel={lastYearLabel}
                          />
                        )}
                      />

                    {/* Current Year Stacked Bars */}
                    {yKeys.map((key, index) => (
                      <Bar
                        key={key}
                        dataKey={key}
                        stackId="currentYear"
                        fill={originColors[key]}
                        name={key}
                        barSize={useCompactBars ? CURRENT_YEAR_BAR_SIZE * 0.75 : CURRENT_YEAR_BAR_SIZE}
                        radius={index === yKeys.length - 1 ? [3, 3, 0, 0] : [0, 0, 0, 0]}
                      />
                    ))}

                    {/* Last Year Stacked Bars (half width) */}
                    {hasLastYearData && yKeys.map((key, index) => (
                      <Bar
                        key={`ly_${key}`}
                        dataKey={`ly_${key}`}
                        stackId="lastYear"
                        fill={lastYearOriginColors[key]}
                        name={`${key} (Last Year)`}
                        barSize={useCompactBars ? LAST_YEAR_BAR_SIZE * 0.75 : LAST_YEAR_BAR_SIZE}
                        radius={index === yKeys.length - 1 ? [2, 2, 0, 0] : [0, 0, 0, 0]}
                        shape={(props) => (
                          <PatternedBar
                            {...props}
                            patternId={lastYearPatternIds[key]}
                            fill={lastYearOriginColors[key]}
                          />
                        )}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-sm" style={{ color: theme.secondaryText }}>
                {selectedOrigins.length === 0
                  ? t('charts.selectAtLeastOneRegion')
                  : t('charts.noDataFilters')}
              </p>
            </div>
          )}
          </div>
          </div>
          </div>

        {/* Legend - Grouped by Year (inside chart card; horizontal padding matches preset title block) */}
        {chartData.length > 0 && selectedOrigins.length > 0 && hideLegend && presetDescription ? (
          <PresetChartLegend theme={theme} fullscreen={presetSingleChartFullscreen}>
            <div className="max-h-40 w-full overflow-y-auto">
              <div className="flex flex-wrap items-center justify-start gap-x-5 gap-y-2">
                {yKeys.map((key) => (
                  <div key={`current-${key}`} className="flex min-w-0 items-start gap-2 max-lg:w-full lg:shrink-0" role="listitem">
                    <svg width={24} height={10} className="shrink-0 block" aria-hidden>
                      <rect x={2} y={1} width={20} height={8} rx={2} fill={originColors[key]} />
                    </svg>
                    <span
                      className="min-w-0 whitespace-normal break-words text-left leading-snug"
                      style={{ fontSize: Math.max(fontSize - 1, 10), color: theme.tickFill }}
                    >
                      {key}
                    </span>
                  </div>
                ))}
                {hasLastYearData && yKeys.map((key, index) => {
                  const legendPatternId = `legend-${lastYearPatternIds[key]}`;
                  return (
                    <div key={`lastyear-${key}`} className="flex min-w-0 items-start gap-2 max-lg:w-full lg:shrink-0" role="listitem">
                      <svg width={24} height={10} className="shrink-0 block" aria-hidden>
                        <defs>
                          {createPatternJSX(legendPatternId, lastYearOriginColors[key], lastYearPatternTypes[index % lastYearPatternTypes.length])}
                        </defs>
                        <rect x={2} y={1} width={20} height={8} rx={2} fill={`url(#${legendPatternId})`} />
                      </svg>
                      <span
                        className="min-w-0 whitespace-normal break-words text-left leading-snug"
                        style={{ fontSize: Math.max(fontSize - 1, 10), color: theme.tickFill }}
                      >
                        {`${key} (${lastYearLabel})`}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </PresetChartLegend>
        ) : chartData.length > 0 && selectedOrigins.length > 0 && (
          <div className={`flex-shrink-0 px-4 pt-2 ${forceFiltersOpen && !presetSingleChartFullscreen ? '' : 'pb-3'}`}>
            <div className="flex flex-wrap items-start justify-between gap-4 gap-y-2">
              <div className="flex flex-wrap gap-6 min-w-0 flex-1">
                {/* Current Year Legend */}
                <div>
                  <p className="text-sm font-bold mb-2" style={{ color: theme.tooltipText }}>{currentYear}</p>
                  <div className="flex flex-wrap gap-3">
                    {yKeys.map((key) => (
                      <div key={`current-${key}`} className="flex items-center gap-1.5">
                        <div
                          className="w-3 h-3 rounded"
                          style={{ backgroundColor: originColors[key] }}
                        />
                        <span className="text-xs" style={{ color: theme.legendText }}>{key}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Last Year Legend */}
                {hasLastYearData && (
                  <div>
                    <p className="text-sm font-bold mb-2" style={{ color: theme.tooltipText }}>{lastYearLabel}</p>
                    <div className="flex flex-wrap gap-3">
                      {yKeys.map((key, index) => (
                        <div key={`lastyear-${key}`} className="flex items-center gap-1.5">
                          <PatternedLegendItem
                            color={lastYearOriginColors[key]}
                            patternId={lastYearPatternIds[key]}
                            patternType={lastYearPatternTypes[index % lastYearPatternTypes.length]}
                            size={12}
                          />
                          <span className="text-xs" style={{ color: theme.legendText }}>{key}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Modals */}
      <FilterModal
        isOpen={typeModalOpen}
        onClose={() => setTypeModalOpen(false)}
        title={t('filters.selectTypes')}
        options={allTypes}
        selectedValues={selectedTypes}
        onSelect={(type) => handleMultiSelect(type, selectedTypes, setSelectedTypes)}
        multiSelect
        onSelectAll={() => setSelectedTypes(allTypes)}
        onClearAll={() => setSelectedTypes([])}
      />

      <FilterModal
        isOpen={varietyModalOpen}
        onClose={() => setVarietyModalOpen(false)}
        title={t('filters.selectVarieties')}
        options={allVarieties}
        selectedValues={selectedVarieties}
        onSelect={(v) => {
          varietiesUserClearedRef.current = false;
          handleMultiSelect(v, selectedVarieties, setSelectedVarieties);
        }}
        multiSelect
        onSelectAll={() => {
          varietiesUserClearedRef.current = false;
          setSelectedVarieties(allVarieties);
        }}
        onClearAll={() => {
          varietiesUserClearedRef.current = true;
          setSelectedVarieties([]);
        }}
      />

      <FilterModal
        isOpen={propertiesModalOpen}
        onClose={() => setPropertiesModalOpen(false)}
        title={t('filters.selectProperties')}
        options={allProperties}
        selectedValues={selectedProperties}
        onSelect={(p) => {
          propertiesUserClearedRef.current = false;
          handleMultiSelect(p, selectedProperties, setSelectedProperties);
        }}
        multiSelect
        onSelectAll={() => {
          propertiesUserClearedRef.current = false;
          setSelectedProperties(allProperties);
        }}
        onClearAll={() => {
          propertiesUserClearedRef.current = true;
          setSelectedProperties([]);
        }}
      />

      <FilterModal
        isOpen={originModalOpen}
        onClose={() => setOriginModalOpen(false)}
        title={t('filters.selectOrigins')}
        options={allOrigins}
        selectedValues={selectedOrigins}
        onSelect={(o) => {
          originsUserClearedRef.current = false;
          handleMultiSelect(o, selectedOrigins, setSelectedOrigins);
        }}
        multiSelect
        onSelectAll={() => {
          originsUserClearedRef.current = false;
          setSelectedOrigins(allOrigins);
        }}
        onClearAll={() => {
          originsUserClearedRef.current = true;
          setSelectedOrigins([]);
        }}
      />

      <FilterModal
        isOpen={groupByModalOpen}
        onClose={() => setGroupByModalOpen(false)}
        title={t('charts.volumeByOrigin.groupBy')}
        options={['daily', 'weekly']}
        selectedValues={viewMode}
        onSelect={(v) => setViewMode(v)}
        displayFormatter={(v) => (v === 'weekly' ? t('charts.volumeByOrigin.week') : t('charts.volumeByOrigin.day'))}
      />
    </div>
  );
};

export default StackedBarChart;
